import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  DangerButton,
  IconButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { SectionHeader, Body } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import SidePanel, { SidePanelHandle } from 'shared/components/side_panel';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  Document,
  Row,
  TalentMatchingApplication,
} from 'talent_matching/types';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import useCandidateSidePanel from 'talent_matching/hooks/useCandidateSidePanel';
import PreviewIcon from '@grnhse/seedling/lib/birch/icons/preview';
import AdvanceButton from 'talent_matching/components/shared/advance_button';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import SidePanelTabs from 'talent_matching/components/side_panel/side_panel_tabs';
import MatchStrengthExplanation from 'talent_matching/components/side_panel/match_strength_explanation';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FeedbackModal from 'talent_matching/components/modals/leave_feedback_modal';
import RowStepper from 'talent_matching/components/side_panel/row_stepper';
import AddTagsModal from 'talent_matching/components/modals/add_tags_modal';
import RejectModal from 'candidate_profile_redesign/modals/reject/reject_modal';
import SidePanelHeader from 'talent_matching/components/side_panel/side_panel_header';
import OverrideMatchScoreModal from 'talent_matching/components/modals/override_match_score_modal';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import {
  EllipsisMenuButton,
  ExpandableMenu,
  MenuItem,
  MenuList,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import CandidateProfileWrapper from './candidate_profile_wrapper';
import EmailCandidateModal from 'candidate_profile_redesign/modals/email_candidate_modal';
import TransferJobModal from 'candidate_profile_redesign/modals/transfer_job_modal';
import ResolveFraudReportModalWrapper from './resolve_fraud_report_modal_wrapper';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';
import FraudReportCollapsible from 'talent_matching/components/side_panel/fraud_report_collapsible';
import LinkedinActivitySection from 'talent_matching/components/side_panel/linkedin_activity_section';
import FullFraudReportModal from 'fraud/components/full_fraud_report_modal';
import { useFraudReport } from 'talent_matching/hooks/use_fraud_report';
import DocumentHeader from 'talent_matching/components/shared/document_header';
import DocumentContent from 'talent_matching/components/shared/document_content';
import DocumentViewerModal from 'talent_matching/components/shared/document_viewer_modal';
import { LinkedinContent, LinkedinHeader } from './linkedin_panel';
import {
  useApplicationViewTracking,
  useEscapeKeyToClose,
  useExtendedPreviewVsModal,
  useDefaultSidePanelTab,
  usePreventScroll,
  useSyncResumePreviewOnCandidateChange,
  useViewportWidth,
} from './candidate_side_panel_hooks';
import { useRejectModal } from '../../../application_review/components/reject_modal_context';

const t = withLocalePrefix('talent_matching');

const CandidateName = styled(SectionHeader)`
  color: ${FullPalette.EVERGREEN_700};
`;

const EXTENDED_GUTTER = 150;
const PANEL_BASE_WIDTH = 650;
const EXTENDED_PREVIEW_MIN_WIDTH = 520;
const EXTENDED_PREVIEW_MAX_WIDTH = 940;
const MIN_EXTENDED_VIEW_WIDTH =
  PANEL_BASE_WIDTH + EXTENDED_PREVIEW_MIN_WIDTH + EXTENDED_GUTTER;

type Props = {
  onClose: () => void;
  applicationId: number;
  onToggle: () => void;
  isVisible?: boolean;
  rowCount?: number;
  currentIndex?: number | null;
  previousCandidate?: Row | null;
  nextCandidate?: Row | null;
  candidatePrefetchBatch: Row[];
  selectCandidate?: (row: Row) => void;
  openCalibrationModal: () => void;
  calibrationModalOpen: boolean;
  onRejectSuccess: (row: Row, closeModal: () => void) => void;
};

const FullHeightStack = styled(Stack)`
  height: 100%;
`;

const PanelContent = styled.div`
  padding: ${Tokens.spacing['3']} ${Tokens.spacing['3']} 0
    ${Tokens.spacing['3']};
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
`;

const Footer = styled.div`
  bottom: 0;
`;

const SidePanelIconButton = styled.div`
  padding-top: ${Tokens.spacing['0.5']};
  flex-shrink: 0;
`;

const PanelLayout = styled.div`
  display: flex;
  height: 100%;
`;

const ResumePreviewContainer = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: ${EXTENDED_PREVIEW_MAX_WIDTH}px;
  background: ${FullPalette.WHITE};
  border-right: 1px solid ${FullPalette.GREY_700};
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ResumePreviewBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: ${Tokens.spacing['3']};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing['2']};
`;

const BorderedFrame = styled.div`
  flex: 1;
  min-height: 0;
  border: 1px solid ${FullPalette.EVERGREEN_E200};
  border-radius: 4px;
  overflow: hidden;
  background: ${FullPalette.WHITE};
`;

const RightPanelColumn = styled(FullHeightStack)`
  flex: 0 0 ${PANEL_BASE_WIDTH}px;
  min-width: ${PANEL_BASE_WIDTH}px;
  max-width: ${PANEL_BASE_WIDTH}px;
`;

const CandidateSidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      candidatePrefetchBatch,
      selectCandidate,
      openCalibrationModal,
      calibrationModalOpen,
      onRejectSuccess,
    },
    ref
  ) => {
    const [extendedResumePreview, setExtendedResumePreview] =
      React.useState<Document | null>(null);
    const [linkedinPreview, setLinkedinPreview] = React.useState<string | null>(
      null
    );
    const [modalDocument, setModalDocument] = React.useState<Document | null>(
      null
    );
    const viewportWidth = useViewportWidth(MIN_EXTENDED_VIEW_WIDTH);
    const {
      rejectIsOpen: rejectModalOpen,
      openReject,
      closeReject: closeRejectModal,
      rejectModalProps,
    } = useRejectModal();

    const {
      paths,
      canAdvance,
      canReject,
      feedbackOnly,
      canCreateTags,
      canViewTalentMatchingAiMatchScore,
      canViewFraudReports,
      calibrationTerms,
      appReviewSessionId,
      linkedinRscEnabled,
      activeSidePanelTab,
      setActiveSidePanelTab,
    } = useUserContext();
    const { hasCalibrationSetting } = useRankingInProgress();

    const panelContentRef = React.useRef<HTMLDivElement>(null);
    usePreventScroll(panelContentRef);
    const updatePerson = useUpdatePerson() as (
      person: TalentMatchingApplication
    ) => void;

    const { handlePanelRef, application, isLoading, panelRef } =
      useCandidateSidePanel({
        applicationId,
        ref,
        prefetchCandidatesBatch: candidatePrefetchBatch,
      });

    useApplicationViewTracking({
      isLoading,
      applicationId: application?.id,
      appReviewSessionId,
    });

    useDefaultSidePanelTab({
      applicationId,
      application,
      isLoading,
      isPanelVisible: isVisible,
      activeSidePanelTab,
      setActiveSidePanelTab,
    });

    const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] =
      useModal();
    const [addTagsIsOpen, openAddTagsModal, closeAddTagsModal] = useModal();
    const [showOverrideModal, openOverrideModal, closeOverrideModal] =
      useModal();
    const [isSendEmailModalOpen, openSendEmailModal, closeSendEmailModal] =
      useModal();
    const [
      isTransferJobModalOpen,
      openTransferJobModal,
      closeTransferJobModal,
    ] = useModal();
    const [showFraudModal, openFraudModal, closeFraudModal] = useModal();

    const { data: fraudReport } = useFraudReport(application);
    const openRejectModal = useCallback(() => {
      openReject();
    }, [openReject]);

    const profileUrl = `/people/${application?.personId}/applications/${application?.id}`;

    const goToCandidateProfile = () => {
      application && window.open(profileUrl, '_blank');
    };

    const disableClickOutside =
      showFeedbackModal ||
      !!modalDocument ||
      rejectModalOpen ||
      addTagsIsOpen ||
      calibrationModalOpen ||
      showOverrideModal ||
      showFraudModal ||
      isSendEmailModalOpen ||
      isTransferJobModalOpen;

    const handleClosePanel = React.useCallback(() => {
      setExtendedResumePreview(null);
      setLinkedinPreview(null);
      onClose();
    }, [onClose]);

    const handleToggle = React.useCallback(() => {
      setExtendedResumePreview(null);
      setLinkedinPreview(null);
      onToggle();
    }, [onToggle]);

    const allowExtendedPreview = viewportWidth >= MIN_EXTENDED_VIEW_WIDTH;

    useSyncResumePreviewOnCandidateChange({
      applicationId: application?.id,
      applicationResumeDocument: application?.resume?.document,
      extendedResumePreview,
      linkedinPreview,
      modalDocument,
      setExtendedResumePreview,
      setLinkedinPreview,
      setModalDocument,
      allowExtendedPreview,
      isVisible: isVisible ?? false,
    });

    useEscapeKeyToClose({
      modalsOpen:
        showFeedbackModal ||
        rejectModalOpen ||
        addTagsIsOpen ||
        calibrationModalOpen ||
        showOverrideModal ||
        showFraudModal,
      extendedResumePreview,
      modalDocument,
      isVisible,
      handleClosePanel,
      setExtendedResumePreview,
      setModalDocument,
    });

    useExtendedPreviewVsModal({
      allowExtendedPreview,
      extendedResumePreview,
      modalDocument,
      setExtendedResumePreview,
      setModalDocument,
    });

    const hasExtendedPreview = extendedResumePreview || linkedinPreview;
    const hasBothPreviews = extendedResumePreview && linkedinPreview;
    const previewCount = hasBothPreviews ? 2 : 1;
    const computedPanelWidth =
      allowExtendedPreview && hasExtendedPreview
        ? `min(${
            PANEL_BASE_WIDTH + EXTENDED_PREVIEW_MAX_WIDTH * previewCount
          }px, calc(100vw - ${EXTENDED_GUTTER}px))`
        : `${PANEL_BASE_WIDTH}px`;

    // Ref to hold the function to open the resolve modal
    const openResolveModalRef = React.useRef<(() => void) | null>(null);

    const fraudReportCollapsible = () => (
      <FraudReportCollapsible
        application={application}
        openFraudModal={openFraudModal}
        openRejectModal={() => {
          openReject({
            rejectionCategory: 'security_risk',
          });
        }}
        canReject={canReject}
        onResolveFraudReport={() => openResolveModalRef.current?.()}
      />
    );

    const matchStrengthExplanation = () => (
      <MatchStrengthExplanation
        application={application}
        openCalibrationModal={openCalibrationModal}
        openOverrideModal={openOverrideModal}
        showExplanationKey="talent_matching/match_strength_explanation"
        hasCalibrationSetting={hasCalibrationSetting}
      />
    );

    const collapsibleComponents = () => {
      const components = [];

      if (canViewTalentMatchingAiMatchScore) {
        components.push(matchStrengthExplanation());
      }

      if (canViewFraudReports) {
        if (application.fraudReportHighRisk) {
          components.unshift(fraudReportCollapsible());
        } else {
          components.push(fraudReportCollapsible());
        }
      }

      return components;
    };

    return (
      <>
        <SidePanel
          ref={handlePanelRef}
          id="side_panel"
          onClose={handleClosePanel}
          isVisible={isVisible}
          disableClickOutside={disableClickOutside}
          width={computedPanelWidth}
          onOutsideClick={() => {
            setExtendedResumePreview(null);
            setLinkedinPreview(null);
            setModalDocument(null);
          }}
        >
          <PanelLayout>
            {allowExtendedPreview && extendedResumePreview && application && (
              <ResumePreviewContainer>
                <ResumePreviewBody>
                  <DocumentHeader
                    document={extendedResumePreview}
                    onClose={() => setExtendedResumePreview(null)}
                    candidate={application}
                    showCandidateActions={false}
                  />
                  <DocumentContent
                    document={extendedResumePreview}
                    hasCalibrationTerms={Boolean(calibrationTerms?.length)}
                  />
                </ResumePreviewBody>
              </ResumePreviewContainer>
            )}
            {allowExtendedPreview && linkedinPreview && (
              <ResumePreviewContainer>
                <ResumePreviewBody>
                  <LinkedinHeader onClose={() => setLinkedinPreview(null)} />
                  <BorderedFrame>
                    <LinkedinContent iframePath={linkedinPreview} />
                  </BorderedFrame>
                </ResumePreviewBody>
              </ResumePreviewContainer>
            )}

            <RightPanelColumn spacing={0} justifyContent="space-between">
              <PanelContent ref={panelContentRef}>
                {isLoading ? (
                  <Stack>
                    <LoadingSpinner size="medium" />
                  </Stack>
                ) : application ? (
                  <FullHeightStack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{
                        width: '100%',
                      }}
                    >
                      <SidePanelHeader application={application} />
                      <SidePanelIconButton>
                        <IconButton
                          icon={CloseIcon}
                          size="md"
                          onClick={handleToggle}
                          label={t('actions.close_panel')}
                          flat
                        />
                      </SidePanelIconButton>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ paddingTop: 1, paddingBottom: 2 }}
                    >
                      {!feedbackOnly && (
                        <>
                          <Tooltip
                            label={
                              canAdvance
                                ? ''
                                : t('table.tooltips.cannot_advance')
                            }
                            ariaLabel={t('table.tooltips.cannot_advance')}
                          >
                            <AdvanceButton
                              application={application}
                              panelRef={panelRef}
                              canAdvance={canAdvance}
                            />
                          </Tooltip>
                          <Tooltip
                            label={
                              canReject ? '' : t('table.tooltips.cannot_reject')
                            }
                            ariaLabel={t('table.tooltips.cannot_reject')}
                          >
                            <DangerButton
                              onClick={() => openRejectModal()}
                              size="md"
                              disabled={!canReject}
                            >
                              {t('reject')}
                            </DangerButton>
                          </Tooltip>
                        </>
                      )}
                      <SecondaryButton onClick={openFeedbackModal} size="md">
                        {application?.userScorecard
                          ? t('edit_feedback')
                          : t('leave_feedback')}
                      </SecondaryButton>
                      <SecondaryButton onClick={openAddTagsModal} size="md">
                        {t('tag')}
                      </SecondaryButton>
                      <IconButton
                        icon={PreviewIcon}
                        label={t('go_to_candidate_profile')}
                        onClick={goToCandidateProfile}
                        size="md"
                      />
                      {/* @ts-expect-error - TS2322 */}
                      <ExpandableMenu dangerouslyBypassScrollLock>
                        <EllipsisMenuButton
                          aria-label={t('table.labels.actions')}
                        />
                        <MenuList>
                          <MenuItem onSelect={openTransferJobModal}>
                            {t('transfer_to_another_job')}
                          </MenuItem>
                          {application?.email && (
                            <MenuItem onSelect={openSendEmailModal}>
                              {t('send_email')}
                            </MenuItem>
                          )}
                        </MenuList>
                      </ExpandableMenu>
                    </Stack>
                    {linkedinRscEnabled && (
                      <LinkedinActivitySection
                        linkedinAddress={application.linkedinAddress}
                        onViewLinkedin={
                          application.linkedinIframePath
                            ? () => {
                                setLinkedinPreview(
                                  application.linkedinIframePath
                                );
                              }
                            : undefined
                        }
                      />
                    )}
                    {collapsibleComponents().map((collapsible) => collapsible)}
                    {!isLoading && (
                      <SidePanelTabs
                        application={application}
                        onOpenDocument={
                          allowExtendedPreview
                            ? (doc) => {
                                setModalDocument(null);
                                setLinkedinPreview(null);
                                setExtendedResumePreview(doc);
                              }
                            : (doc) => {
                                setExtendedResumePreview(null);
                                setLinkedinPreview(null);
                                setModalDocument(doc);
                              }
                        }
                        openFeedbackModal={openFeedbackModal}
                      />
                    )}
                  </FullHeightStack>
                ) : (
                  <Stack spacing={1}>
                    <CandidateName>{t('panel.error.title')}</CandidateName>
                    <Body>{t('panel.error.message')}</Body>
                  </Stack>
                )}
              </PanelContent>

              <Footer>
                <RowStepper
                  currentIndex={currentIndex}
                  rowCount={rowCount}
                  previousCandidate={previousCandidate}
                  nextCandidate={nextCandidate}
                  selectCandidate={selectCandidate}
                />
              </Footer>
            </RightPanelColumn>
          </PanelLayout>
        </SidePanel>

        {application && (
          <>
            <FeedbackModal
              isOpen={showFeedbackModal}
              closeModal={closeFeedbackModal}
              application={application}
              hiringPlanTagsPath={paths.hiringPlanTagsPath}
            />
            <AddTagsModal
              isOpen={addTagsIsOpen}
              closeModal={closeAddTagsModal}
              getTagsPath={paths.getTagsPath}
              addTagsPath={application.addTagsPath}
              fullName={application.fullName}
              currentTags={application.tags}
              application={application}
              canCreateTags={canCreateTags}
            />
            <RejectModal
              bulkMode={false}
              closeRejectModal={closeRejectModal}
              isRejectModalOpen={rejectModalOpen}
              getRejectModalPath={application.rejectModalPath}
              candidateName={application.fullName}
              changeApplicationStatePath={application.rejectPath}
              rejectionTrackingPayload={{
                app_review_session_id: appReviewSessionId,
                source: 'talent_matching',
                action_source: 'side_panel',
              }}
              onRejectSuccess={() => {
                onRejectSuccess(application, closeRejectModal);

                if (panelRef?.current) {
                  panelRef.current.close();
                }

                setModalDocument(null);

                if (extendedResumePreview) {
                  setExtendedResumePreview(null);
                }
              }}
              {...rejectModalProps}
            />
            <OverrideMatchScoreModal
              isOpen={showOverrideModal}
              onClose={closeOverrideModal}
              application={application as TalentMatchingApplication}
              updatePerson={updatePerson}
            />
            <CandidateProfileWrapper
              candidateProfileContextPath={
                application.candidateProfileContextPath
              }
            >
              <ResolveFraudReportModalWrapper
                fraudReport={fraudReport ?? null}
                applicationId={application.id}
                onModalOpenReady={(openFn) => {
                  openResolveModalRef.current = openFn;
                }}
              />
              <TransferJobModal
                isOpen={isTransferJobModalOpen}
                onDismiss={closeTransferJobModal}
                hiringPlansPath={application.transferModalHiringPlansPath}
                ownerOptionsPath={application.transferModalOwnerOptionsPath}
                submitPath={application.addPersonToJobPath}
              />
              <EmailCandidateModal
                isOpen={isSendEmailModalOpen}
                handleClose={closeSendEmailModal}
                modalPath={application.emailCandidatePath}
                title={t('send_email_to', { name: application.fullName })}
              />
            </CandidateProfileWrapper>
            {fraudReport && (
              <FullFraudReportModal
                candidateName={application.fullName}
                report={fraudReport}
                isOpen={showFraudModal}
                closeModal={closeFraudModal}
              />
            )}

            {modalDocument && (
              <DocumentViewerModal
                document={modalDocument}
                onClose={() => setModalDocument(null)}
                openFeedbackModal={openFeedbackModal}
                openRejectModal={openRejectModal}
                application={application}
                panelRef={panelRef}
                canAdvance={canAdvance}
                canReject={canReject}
              />
            )}
          </>
        )}
      </>
    );
  }
);

export default CandidateSidePanel;
